/**
 * Coach Effectiveness Analytics Page (Phase 21.8.6a)
 *
 * Tabbed analytics dashboard for AI Coach usage metrics.
 * - Adoption tab: session counts, mode distribution, cost, trends
 * - Behavioral tab: placeholder for Phase 21.8.6b
 * - SOP Insights tab: placeholder for Phase 21.8.6c
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BotMessageSquare,
  ChevronLeft,
  Loader2,
  Calendar,
  Users,
  MessageCircle,
  CheckCircle2,
  RotateCcw,
  TrendingUp,
  AlertTriangle,
  Coins,
  Info,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import {
  useCoachAdoption,
  type CoachAdoptionData,
} from '../../hooks/useCoachAnalytics';

// ============================================
// CONSTANTS
// ============================================

type TabId = 'adoption' | 'behavioral' | 'sop-insights';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 'adoption', label: 'Adoption' },
  { id: 'behavioral', label: 'Behavioral' },
  { id: 'sop-insights', label: 'SOP Insights' },
];

const PERIOD_OPTIONS = [
  { value: 0, label: 'All Time' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last year' },
];

const MODE_LABELS: Record<string, string> = {
  pre_shift: 'Pre-Shift Prep',
  debrief: 'Debrief',
  sop_help: 'SOP Help',
  quiz_review: 'Quiz Review',
};

const MODE_COLORS: Record<string, string> = {
  pre_shift: '#00674a',
  debrief: '#3b82f6',
  sop_help: '#8b5cf6',
  quiz_review: '#f59e0b',
};

// Fallback for unknown modes
const FALLBACK_COLOR = '#6b7280';

// ============================================
// HELPERS
// ============================================

function formatTokenCount(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    return `${Math.round(tokens / 1_000)}K`;
  }
  return String(tokens);
}

function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getModeLabel(mode: string): string {
  return MODE_LABELS[mode] ?? mode;
}

function getModeColor(mode: string): string {
  return MODE_COLORS[mode] ?? FALLBACK_COLOR;
}

// ============================================
// STAT CARD COMPONENT
// ============================================

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: typeof Users;
  iconBg: string;
  iconColor: string;
  isLoading?: boolean;
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconBg,
  iconColor,
  isLoading,
}: StatCardProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg',
            iconBg
          )}
        >
          <Icon size={20} className={iconColor} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          {isLoading ? (
            <div className="mt-1 h-7 w-20 animate-pulse rounded bg-gray-200" />
          ) : (
            <p className="text-2xl font-bold text-gray-900 tabular-nums">
              {value}
            </p>
          )}
          {subtitle && (
            <p className="text-xs text-gray-400">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// ADOPTION TAB CONTENT
// ============================================

function AdoptionTab({
  data,
  isLoading,
}: {
  data: CoachAdoptionData | undefined;
  isLoading: boolean;
}) {
  const summary = data?.summary;
  const modeDistribution = data?.modeDistribution ?? [];
  const sessionsOverTime = data?.sessionsOverTime ?? [];

  // Empty state: no sessions at all
  if (!isLoading && summary && summary.totalSessions === 0) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <div className="mx-auto max-w-lg text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50">
            <BotMessageSquare size={32} className="text-emerald-600" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">
            No coaching sessions yet
          </h3>
          <p className="mt-2 text-sm text-gray-500 leading-relaxed">
            To drive adoption, ensure operators know about the Coach tab in their
            portal. Coach sessions help operators reflect on their performance
            before shifts and after assessments.
          </p>
          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-left">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
              <Info size={16} />
              Tips to increase adoption
            </h4>
            <ul className="mt-2 space-y-1 text-sm text-emerald-700">
              <li>Mention the Coach during operator onboarding</li>
              <li>Encourage pre-shift prep sessions before each shift</li>
              <li>Recommend debrief sessions after assessments</li>
              <li>Have operators try quiz review after practice quizzes</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Pie chart tooltip
  const PieTooltipContent = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{
      payload: { mode: string; count: number };
    }>;
  }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const total = modeDistribution.reduce((sum, m) => sum + m.count, 0);
    const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <p className="font-medium text-gray-900">{getModeLabel(d.mode)}</p>
        <p className="text-sm text-gray-600">
          {d.count} session{d.count !== 1 ? 's' : ''} ({pct}%)
        </p>
      </div>
    );
  };

  // Area chart tooltip
  const AreaTooltipContent = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ value: number }>;
    label?: string;
  }) => {
    if (!active || !payload?.length || !label) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <p className="mb-1 text-xs font-medium text-gray-500">
          {new Date(label).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
        <p className="text-sm font-semibold text-gray-900">
          {payload[0].value} session{payload[0].value !== 1 ? 's' : ''}
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Primary Stat Cards (4-col) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Sessions"
          value={isLoading ? '--' : String(summary?.totalSessions ?? 0)}
          icon={BotMessageSquare}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-700"
          isLoading={isLoading}
        />
        <StatCard
          title="Active Coaches"
          value={
            isLoading
              ? '--'
              : `${summary?.uniqueOperators ?? 0} of ${summary?.totalActiveOperators ?? 0}`
          }
          subtitle="operators using coach"
          icon={Users}
          iconBg="bg-blue-100"
          iconColor="text-blue-700"
          isLoading={isLoading}
        />
        <StatCard
          title="Completion Rate"
          value={
            isLoading
              ? '--'
              : summary?.completionRate != null
                ? `${summary.completionRate}%`
                : '--'
          }
          subtitle="sessions completed"
          icon={CheckCircle2}
          iconBg="bg-green-100"
          iconColor="text-green-700"
          isLoading={isLoading}
        />
        <StatCard
          title="Avg Messages"
          value={
            isLoading
              ? '--'
              : summary?.avgMessagesPerSession != null
                ? summary.avgMessagesPerSession.toFixed(1)
                : '--'
          }
          subtitle="per session"
          icon={MessageCircle}
          iconBg="bg-purple-100"
          iconColor="text-purple-700"
          isLoading={isLoading}
        />
      </div>

      {/* Secondary Stat Cards (3-col) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          title="Sessions / Operator / Week"
          value={
            isLoading
              ? '--'
              : summary?.sessionsPerOperatorPerWeek != null
                ? summary.sessionsPerOperatorPerWeek.toFixed(1)
                : '--'
          }
          icon={TrendingUp}
          iconBg="bg-indigo-100"
          iconColor="text-indigo-700"
          isLoading={isLoading}
        />
        <StatCard
          title="Return Rate"
          value={
            isLoading
              ? '--'
              : summary?.returnRate != null
                ? `${summary.returnRate}%`
                : '--'
          }
          subtitle="operators with >1 session"
          icon={RotateCcw}
          iconBg="bg-amber-100"
          iconColor="text-amber-700"
          isLoading={isLoading}
        />
        <StatCard
          title="Escalation Rate"
          value={
            isLoading
              ? '--'
              : summary?.escalationRate != null
                ? `${summary.escalationRate}%`
                : '--'
          }
          subtitle="sessions escalated"
          icon={AlertTriangle}
          iconBg={
            summary?.escalationRate && summary.escalationRate > 0
              ? 'bg-red-100'
              : 'bg-gray-100'
          }
          iconColor={
            summary?.escalationRate && summary.escalationRate > 0
              ? 'text-red-700'
              : 'text-gray-500'
          }
          isLoading={isLoading}
        />
      </div>

      {/* Token Cost Card */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
            <Coins size={16} className="text-amber-700" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Token Usage & Cost</h3>
            <p className="text-sm text-gray-500">
              Claude Sonnet 4.6 API consumption
            </p>
          </div>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="flex h-20 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Input Tokens
                </p>
                <p className="mt-1 text-lg font-bold text-gray-900 tabular-nums">
                  {formatTokenCount(summary?.tokenCost?.inputTokens ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Output Tokens
                </p>
                <p className="mt-1 text-lg font-bold text-gray-900 tabular-nums">
                  {formatTokenCount(summary?.tokenCost?.outputTokens ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Cache Read
                </p>
                <p className="mt-1 text-lg font-bold text-gray-900 tabular-nums">
                  {formatTokenCount(summary?.tokenCost?.cacheReadTokens ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Cache Creation
                </p>
                <p className="mt-1 text-lg font-bold text-gray-900 tabular-nums">
                  {formatTokenCount(
                    summary?.tokenCost?.cacheCreationTokens ?? 0
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Estimated Cost
                </p>
                <p className="mt-1 text-lg font-bold text-emerald-700 tabular-nums">
                  {formatUsd(summary?.tokenCost?.estimatedCostUsd ?? 0)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Mode Distribution Pie Chart */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-6 py-4">
            <h3 className="font-semibold text-gray-900">Mode Distribution</h3>
            <p className="text-sm text-gray-500">
              Sessions by coaching mode
            </p>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : modeDistribution.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                  <BotMessageSquare className="h-7 w-7 text-gray-400" />
                </div>
                <p className="mt-3 text-sm font-medium text-gray-900">
                  No data available
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <div className="h-64 flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={modeDistribution as Array<{ mode: string; count: number; [key: string]: string | number }>}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="count"
                        nameKey="mode"
                      >
                        {modeDistribution.map((entry) => (
                          <Cell
                            key={entry.mode}
                            fill={getModeColor(entry.mode)}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltipContent />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Legend */}
                <div className="space-y-3">
                  {modeDistribution.map((entry) => (
                    <div
                      key={entry.mode}
                      className="flex items-center gap-2"
                    >
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: getModeColor(entry.mode) }}
                      />
                      <span className="text-sm text-gray-700">
                        {getModeLabel(entry.mode)}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 tabular-nums">
                        {entry.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sessions Over Time Area Chart */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-6 py-4">
            <h3 className="font-semibold text-gray-900">Sessions Over Time</h3>
            <p className="text-sm text-gray-500">
              Daily coaching session count
            </p>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : sessionsOverTime.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                  <TrendingUp className="h-7 w-7 text-gray-400" />
                </div>
                <p className="mt-3 text-sm font-medium text-gray-900">
                  No data available
                </p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={sessionsOverTime}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="coachSessionsGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#00674a"
                          stopOpacity={0.2}
                        />
                        <stop
                          offset="95%"
                          stopColor="#00674a"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f0f0f0"
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<AreaTooltipContent />} />
                    <Area
                      type="monotone"
                      dataKey="sessions"
                      stroke="#00674a"
                      strokeWidth={2}
                      fill="url(#coachSessionsGradient)"
                      dot={false}
                      activeDot={{
                        r: 4,
                        strokeWidth: 2,
                        fill: '#fff',
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMING SOON PLACEHOLDER
// ============================================

function ComingSoonPlaceholder({ tabName }: { tabName: string }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-12 shadow-sm">
      <div className="mx-auto max-w-md text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
          <Info size={24} className="text-gray-400" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-gray-900">
          {tabName} - Coming Soon
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          This analytics view is under development and will be available in a
          future update.
        </p>
      </div>
    </div>
  );
}

// ============================================
// PAGE COMPONENT
// ============================================

export function CoachEffectivenessPage() {
  useDocumentTitle('Coach Effectiveness');
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabId>('adoption');
  const [period, setPeriod] = useState(30);

  // Fetch data
  const { data, isLoading } = useCoachAdoption(period);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} />
            Analytics
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <BotMessageSquare size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Coach Effectiveness
              </h1>
              <p className="text-sm text-gray-500">
                AI Coach usage, adoption metrics, and cost governance
              </p>
            </div>
          </div>
        </div>

        {/* Period Filter */}
        <div className="relative">
          <Calendar
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-8 text-sm text-gray-700 transition-colors duration-200 hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'border-b-2 pb-3 pt-1 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-guild-green text-guild-green'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'adoption' && (
        <AdoptionTab data={data} isLoading={isLoading} />
      )}
      {activeTab === 'behavioral' && (
        <ComingSoonPlaceholder tabName="Behavioral Analytics" />
      )}
      {activeTab === 'sop-insights' && (
        <ComingSoonPlaceholder tabName="SOP Insights" />
      )}
    </div>
  );
}
